import React, { FC, useEffect, useState } from 'react';
import { InputWrapper, InputProps, ErrorText } from './DefaultInput';
import styled, { css } from 'styled-components';
import { Colors } from '../../../styles/colors';
import moment from 'moment';
import DateTime from 'react-datetime';
import { Label } from './Label';
import { Icon } from '../Icon';
import { isValidYear, isValidMonthYear, isValidDayMonthYear } from '../../../utils/Validators';

//NOTE locale config code, copied from moment/locale/de v2.29.0. copied here due to import problems
//@ts-ignore
function processRelativeTime(number, withoutSuffix, key, isFuture) {
  var format = {
    m: ['eine Minute', 'einer Minute'],
    h: ['eine Stunde', 'einer Stunde'],
    d: ['ein Tag', 'einem Tag'],
    dd: [number + ' Tage', number + ' Tagen'],
    w: ['eine Woche', 'einer Woche'],
    M: ['ein Monat', 'einem Monat'],
    MM: [number + ' Monate', number + ' Monaten'],
    y: ['ein Jahr', 'einem Jahr'],
    yy: [number + ' Jahre', number + ' Jahren'],
  };
  return withoutSuffix ? format[key][0] : format[key][1];
}
moment.defineLocale('de', {
  months: 'Januar_Februar_März_April_Mai_Juni_Juli_August_September_Oktober_November_Dezember'.split('_'),
  monthsShort: 'Jan._Feb._März_Apr._Mai_Juni_Juli_Aug._Sep._Okt._Nov._Dez.'.split('_'),
  monthsParseExact: true,
  weekdays: 'Sonntag_Montag_Dienstag_Mittwoch_Donnerstag_Freitag_Samstag'.split('_'),
  weekdaysShort: 'So._Mo._Di._Mi._Do._Fr._Sa.'.split('_'),
  weekdaysMin: 'So_Mo_Di_Mi_Do_Fr_Sa'.split('_'),
  weekdaysParseExact: true,
  longDateFormat: {
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
    L: 'DD.MM.YYYY',
    LL: 'D. MMMM YYYY',
    LLL: 'D. MMMM YYYY HH:mm',
    LLLL: 'dddd, D. MMMM YYYY HH:mm',
  },
  calendar: {
    sameDay: '[heute um] LT [Uhr]',
    sameElse: 'L',
    nextDay: '[morgen um] LT [Uhr]',
    nextWeek: 'dddd [um] LT [Uhr]',
    lastDay: '[gestern um] LT [Uhr]',
    lastWeek: '[letzten] dddd [um] LT [Uhr]',
  },
  relativeTime: {
    future: 'in %s',
    past: 'vor %s',
    s: 'ein paar Sekunden',
    ss: '%d Sekunden',
    m: processRelativeTime,
    mm: '%d Minuten',
    h: processRelativeTime,
    hh: '%d Stunden',
    d: processRelativeTime,
    dd: processRelativeTime,
    w: processRelativeTime,
    ww: '%d Wochen',
    M: processRelativeTime,
    MM: processRelativeTime,
    y: processRelativeTime,
    yy: processRelativeTime,
  },
  dayOfMonthOrdinalParse: /\d{1,2}\./,
  //@ts-ignore
  ordinal: '%d.',
  week: {
    dow: 1, // Monday is the first day of the week.
    doy: 4, // The week that contains Jan 4th is the first week of the year.
  },
});
moment.locale('de');
//NOTE react-datettime style. copied and extended from react-datetime 3.0.4. copied here due to import problems
const DateTimeStyle = css`
  /*!
  * https://github.com/arqex/react-datetime
  */

  .rdt {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: stretch;
  }
  .rdt input {
    outline: none;
    border: 1px solid ${Colors.background};
    min-height: 3rem;
    color: ${Colors.grayDark};
    background-color: ${Colors.background};
    font-size: 1rem;
    padding: 0 1rem;
    z-index: 2;
    margin: 0;
    flex: 1;
  }
  input::placeholder,
  ::-webkit-input-placeholder {
    color: ${Colors.gray};
    font-size: 1rem;
  }
  input.localError {
    border-color: ${Colors.error};
    padding-left: 1rem;
    color: ${Colors.error};
  }

  .rdt:disabled {
    pointer-events: none;
    cursor: default;
  }
  .rdt ::placeholder {
    color: ${Colors.gray};
  }
  .rdtPicker {
    display: none;
    position: absolute;
    min-width: 250px;
    padding: 4px;
    margin-top: 1px;
    z-index: 99999 !important;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #f9f9f9;
    top: 3rem;
  }
  .rdtOpen .rdtPicker {
    display: block;
  }
  .rdtStatic .rdtPicker {
    box-shadow: none;
    position: static;
  }

  .rdtPicker .rdtTimeToggle {
    text-align: center;
  }

  .rdtPicker table {
    width: 100%;
    margin: 0;
  }
  .rdtPicker td,
  .rdtPicker th {
    text-align: center;
    height: 28px;
  }
  .rdtPicker td {
    cursor: pointer;
  }
  .rdtPicker td.rdtDay:hover,
  .rdtPicker td.rdtHour:hover,
  .rdtPicker td.rdtMinute:hover,
  .rdtPicker td.rdtSecond:hover,
  .rdtPicker .rdtTimeToggle:hover {
    background: #eeeeee;
    cursor: pointer;
  }
  .rdtPicker td.rdtOld,
  .rdtPicker td.rdtNew {
    color: #999999;
  }
  .rdtPicker td.rdtToday {
    position: relative;
  }
  .rdtPicker td.rdtToday:before {
    content: '';
    display: inline-block;
    border-left: 7px solid transparent;
    border-bottom: 7px solid #428bca;
    border-top-color: rgba(0, 0, 0, 0.2);
    position: absolute;
    bottom: 4px;
    right: 4px;
  }
  .rdtPicker td.rdtActive,
  .rdtPicker td.rdtActive:hover {
    background-color: #428bca;
    color: #fff;
    text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.25);
  }
  .rdtPicker td.rdtActive.rdtToday:before {
    border-bottom-color: #fff;
  }
  .rdtPicker td.rdtDisabled,
  .rdtPicker td.rdtDisabled:hover {
    background: none;
    color: #999999;
    cursor: not-allowed;
  }

  .rdtPicker td span.rdtOld {
    color: #999999;
  }
  .rdtPicker td span.rdtDisabled,
  .rdtPicker td span.rdtDisabled:hover {
    background: none;
    color: #999999;
    cursor: not-allowed;
  }
  .rdtPicker th {
    border-bottom: 1px solid #f9f9f9;
  }
  .rdtPicker .dow {
    width: 14.2857%;
    border-bottom: none;
    cursor: default;
  }
  .rdtPicker th.rdtSwitch {
    width: 100px;
  }
  .rdtPicker th.rdtNext,
  .rdtPicker th.rdtPrev {
    font-size: 21px;
    vertical-align: top;
  }

  .rdtPrev span,
  .rdtNext span {
    display: block;
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Chrome/Safari/Opera */
    -khtml-user-select: none; /* Konqueror */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none;
  }

  .rdtPicker th.rdtDisabled,
  .rdtPicker th.rdtDisabled:hover {
    background: none;
    color: #999999;
    cursor: not-allowed;
  }
  .rdtPicker thead tr:first-of-type th {
    cursor: pointer;
  }
  .rdtPicker thead tr:first-of-type th:hover {
    background: #eeeeee;
  }

  .rdtPicker tfoot {
    border-top: 1px solid #f9f9f9;
  }

  .rdtPicker button {
    border: none;
    background: none;
    cursor: pointer;
  }
  .rdtPicker button:hover {
    background-color: #eee;
  }

  .rdtPicker thead button {
    width: 100%;
    height: 100%;
  }

  td.rdtMonth,
  td.rdtYear {
    height: 50px;
    width: 25%;
    cursor: pointer;
  }
  td.rdtMonth:hover,
  td.rdtYear:hover {
    background: #eee;
  }

  .rdtCounters {
    display: inline-block;
  }

  .rdtCounters > div {
    float: left;
  }

  .rdtCounter {
    height: 100px;
  }

  .rdtCounter {
    width: 40px;
  }

  .rdtCounterSeparator {
    line-height: 100px;
  }

  .rdtCounter .rdtBtn {
    height: 40%;
    line-height: 40px;
    cursor: pointer;
    display: block;

    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Chrome/Safari/Opera */
    -khtml-user-select: none; /* Konqueror */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none;
  }
  .rdtCounter .rdtBtn:hover {
    background: #eee;
  }
  .rdtCounter .rdtCount {
    height: 20%;
    font-size: 1.2em;
  }

  .rdtMilli {
    vertical-align: middle;
    padding-left: 8px;
    width: 48px;
  }

  .rdtMilli input {
    width: 100%;
    font-size: 1.2em;
    margin-top: 37px;
  }

  .rdtTime td {
    cursor: default;
  }
`;

export interface CalendarProps extends Omit<InputProps, 'onChange' | 'type'> {
  type: 'day-month-year' | 'month-year' | 'year';
  onChange: (event: string | moment.Moment) => void;
}
const ExtendedInputWrapper = styled(InputWrapper)`
  ${DateTimeStyle}
`;
const IconWrapper = styled.div`
  position: relative;
  right: 0.5rem;
  z-index: 5;
  top: -2.3rem;
  align-self: flex-end;
  height: 0;
  pointer-events: none;
`;

export const DateInput: FC<CalendarProps> = (props: CalendarProps) => {
  let format = 'DD.MM.YYYY';
  if (props.type == 'month-year') format = 'MM.YYYY';
  else if (props.type == 'year') format = 'YYYY';

  const [localError, setLocalError] = useState<boolean>(props.hasError || false);
  useEffect(() => {
    setLocalError(props.hasError || false);
  }, [props.hasError]);

  useEffect(() => {
    if (props.value && !hasError(props.value)) {
      setLocalError(false);
    }
  }, [props.value]);

  const handleLocalError = (value: string | moment.Moment) => {
    if (props.required) {
      setLocalError(hasError(value));
    }
  };
  const hasError = (value: string | moment.Moment) => {
    let toValidate = '';
    if (props.type == 'year') {
      if (moment.isMoment(value)) toValidate = `${value.get('year')}`;
      else toValidate = value;
      return !isValidYear(toValidate);
    } else if (props.type == 'month-year') {
      if (moment.isMoment(value)) toValidate = `${value.get('month') + 1}.${value.get('year')}`;
      else toValidate = value;
      return !isValidMonthYear(toValidate);
    } else {
      //type == 'day-month-year'
      if (moment.isMoment(value)) toValidate = `${value.date()}.${value.get('month') + 1}.${value.get('year')}`;
      else toValidate = value;
      return !isValidDayMonthYear(toValidate);
    }
  };

  return (
    <ExtendedInputWrapper>
      <Label hint={props.hint} for={props.id}>{props.label || 'Datum'}</Label>
      <DateTime
        dateFormat={format}
        timeFormat={false}
        locale="de"
        inputProps={{
          className: localError ? 'localError' : '',
          placeholder: props.placeholder || '',
          onBlur: (event) => handleLocalError(props.value),
        }}
        onChange={(event: string | moment.Moment) => {
          handleLocalError(event);
          props.onChange(event);
        }}
        value={props.value}
        closeOnSelect={true}
      />
      <IconWrapper>
        <Icon icon={'calendar'} size={24} color={localError ? `${Colors.error}` : 'inherit'} />
      </IconWrapper>
      {localError && <ErrorText>{props.errorMessage || `Ungültiger Wert`}</ErrorText>}
    </ExtendedInputWrapper>
  );
};
